import { useState, useMemo, useEffect, useRef } from "react"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  TextField,
} from "@mui/material"
import LoadingSpinner from "../Loaders/LoadingSpinner"
import { Gauge } from "@mui/x-charts"
import Split from "react-split"
import { UserGroupIcon, PlayIcon, ChartBarIcon, PencilIcon, TagIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon } from "@heroicons/react/24/outline"
import { TrashIcon, FolderIcon, ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon } from "@heroicons/react/24/outline"
import FolderDrawerTreeView from "../FolderDrawerTreeView"
import TableComponent from "../TableComponent"
import CustomChip from "../chips/CustomChip"
import ChipComponent from "../chips/ChipComponent/ChipComponent"
import { getPriorityVariant, getAutomationVariant, getTestGroupStatusVariant, getTestCaseTypeVariant, getAutomationStatusVariant } from "../chips/ChipComponent/chipVariants"
import CreateTestLaunchModal from '../Modal/CreateTestLaunchModal';
import { getAssociatedTestCasesForGroup, getTestGroupById, fetchModulesTestGroupWithCount, getTestGroupSummary, getTestGroupAnalytics, removeTestCasesFromTestGroup, reorderTestCaseInGroup } from '../../utils/api/testgroup_apis';
import { getTestLaunchesForGroup } from '../../utils/api/testlaunch_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useParams } from "react-router-dom";
import { MissingData } from "../../enums/enumRegistry";
import { BarChartPro } from '@mui/x-charts-pro';
import { LineChart } from '@mui/x-charts';
import CreateTestGroupModal from '../TestGroupLibrary/CreateTestGroupModal';
import AddTestCasesToGroupModal from '../Modal/AddTestCasesToGroupModal';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import LaunchesTable from './LaunchesTable';
import TabView from '../tabView/TabView';
import { dateFormat } from '../../utils/date';
import { PieChartPro } from '@mui/x-charts-pro';
import CircularProgress from '@mui/material/CircularProgress';
import RowActionMenu from '../menu/RowActionMenu';
import ConfirmationModal from '../Modal/ConfirmationModal';
import ModalWrapper from '../ModalWrapper';
import { CommonFooter } from '../FormComponent';
import { useSnackbar } from "../../utils/useSnackbar";
import { TestGroupStatus } from "../../enums/TestGroupEnums";
import { renderTableCell } from "../../utils/commonfunctions";
import { useDispatch, useSelector } from "react-redux"
import { useTheme, useMediaQuery } from '@mui/material';
import { breakpoints } from "../../theme/theme";
import DEFAULT_ROUTES from "../../constants/routes"
import { useNavigate , Link } from 'react-router-dom';
import { setActivePage } from "../../features/navigation/navigationSlice"
import PageHeader from "../Header/PageHeader"
import ButtonComponent from "../customButton";
// Styles
import styles from '../../styles/testGroupDetail/index.module.scss';
import commonStyles from '../../styles/component.module.scss'
import { formatStatusKey } from "../TestLaunchDetail/statusColorPallete"
import useQueryParams from "../../utils/useQueryParams"
import { useResettablePagination } from "../../hooks/useResettablePagination"
import { OVERVIEW_EMPTY_CARDS } from './overviewEmptyCards.config';
import TestGroupCasesEmptyPanel from './TestGroupCasesEmptyPanel';
import OverviewEmptyCardContent from './OverviewEmptyCardContent';
import TestGroupLaunchNudgeBanner from './TestGroupLaunchNudgeBanner';


// Build module tree from test cases
function buildModuleTree(testCases) {
  const modules = Array.from(new Set(testCases.map((tc) => tc.module)))
  return [...modules.map((mod) => ({ id: mod, name: mod, children: [] }))]
}

// Move tableColumns definition inside the component

function OverviewCard({ title, value, icon, color = "primary", children, trendLabel, trendValue }) {
  const hasValue = value !== undefined && value !== null && value !== '';

  return (
    <Card className={styles['overview-card']}>
      <CardContent className={styles['card-content']}>
        <div className={styles['card-header']}>
          <div>
            <p className={styles['card-title']}>{title}</p>
            {hasValue && <p className={styles['card-value']}>{value}</p>}
          </div>
          <div
            className={styles['card-icon']}
            style={{
              backgroundColor: `${color === 'primary' ? '#EFF6FF' : color === 'success' ? '#F0FDF4' : color === 'warning' ? '#FEF3C7' : '#F3E8FF'}`,
              color: `${color === 'primary' ? '#2563EB' : color === 'success' ? '#16A34A' : color === 'warning' ? '#D97706' : '#9333EA'}`,
            }}
          >
            {icon}
          </div>
        </div>

        <div className={styles['card-body']}>
          {children}

          {trendLabel && trendValue && (
            <div className={styles['card-footer']}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="#475569" fontWeight={600}>
                  {trendLabel}
                </Typography>
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  {trendValue}
                </Typography>
              </Stack>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TestCaseSummaryCard({ summary }) {
  const automatedPercentage = Math.round((summary.automated || 0) / (summary.total || 1) * 100)

  return (
    <OverviewCard
      title="Test Cases Summary"
      value={summary.total}
      icon={<UserGroupIcon className='icon' />}
      color="primary"
    >
      <Stack spacing={1.5}>
        {/* Breakdown */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#21c45d" }} />
            <Typography variant="caption" color="#475569">
              Automated
            </Typography>
          </Stack>
          <Typography variant="caption" fontWeight={600}>
            {summary.automated}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#0ea5e9" }} />
            <Typography variant="caption" color="#475569">
              Manual
            </Typography>
          </Stack>
          <Typography variant="caption" fontWeight={600}>
            {summary.manual}
          </Typography>
        </Stack>

      </Stack>
      {/* Progress Bar at the bottom of the card */}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" sx={{ width: "100%", height: 6, borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              width: `${automatedPercentage}%`,
              bgcolor: "#21c45d",
              borderRadius: "3px 0 0 3px",
            }}
          />
          <Box
            sx={{
              width: `${100 - automatedPercentage}%`,
              bgcolor: "#0ea5e9",
              borderRadius: "0 3px 3px 0",
            }}
          />
        </Stack>
        <Typography variant="caption" color="#475569" sx={{ mt: 1 }}>
          {automatedPercentage}% automated
        </Typography>
      </Box>
    </OverviewCard>
  )
}

function TagBreakdownCard({ moduleTree, total }) {
  // Only use parent modules (no children, skip 'default')
  const parentModules = moduleTree.filter(node => node.id !== 'default');

    // Color palette for pie slices
    const pieColors = [
      '#21c45d',
      '#0ea5e9',
      '#ca8a04',
      '#dc2626',
      '#9234eb',
      '#10b981',
      '#f59e0b',
      '#ef4444',
    ];
  // Prepare data for PieChart
  const pieData = parentModules.map((mod, idx) => ({
    id: idx,
    value: mod.totalcount || 0,
    label: mod.name,
    color: pieColors[idx % pieColors.length],
  }));

  return (
    <OverviewCard
      title="Test Cases by Module"
      value={`${parentModules.length} Modules`}
      icon={<TagIcon className='icon' />} 
      color="secondary"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', minHeight: 160 }}>
        <PieChartPro
          series={[{
            data: pieData,
            arcLabel: 'value',
            arcLabelMinAngle: 15,
          }]}
          width={160}
          height={160}
          hideLegend={true}
        />
      </Box>
      {/* Total */}
      <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="#475569">
          Total: {total} test cases
        </Typography>
      </Box>
    </OverviewCard>
  );
}

function TestLaunchesCard({ launches }) {
  const maxCount = Math.max(...launches.byMonth.map((d) => d.count))

  return (
    <OverviewCard
      title="Test Launches (3 Months)"
      value={launches.total}
      icon={<PlayIcon className='icon' />}
      color="success"
      trendLabel="Trend:"
      trendValue={launches.trend}
    >
      <Stack spacing={1.5}>
        {launches.byMonth.map((monthData, index) => {
          const percentage = maxCount === 0 ? 0 : (monthData.count / maxCount) * 100

          return (
            <Box key={index}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={600} color="#0f172a">
                  {monthData.month}
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  {monthData.count}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: "#21c45d",
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          )
        })}
      </Stack>
    </OverviewCard>
  )
}

function ExecutionStatusCard({ status }) {
  const passRate = Math.round((status.passed / status.total) * 100) || 0;

  // Helper function to get status display value
  const getStatusDisplayValue = (statusValue) => {
    switch (statusValue) {
      case TestGroupStatus.PASSED:
        return 'Passed';
      case TestGroupStatus.FAILED:
        return 'Failed';
      case TestGroupStatus.IN_PROGRESS:
        return 'In Progress';
      case TestGroupStatus.PENDING:
        return 'Pending';
      case TestGroupStatus.ABORTED:
        return 'Aborted';
      default:
        return statusValue;
    }
  };
  const data = [];

  if(status.passed > 0) {
    data.push({ label: 'Passed', value: status.passed, color: '#2e7d32' });
  }
  if(status.failed > 0) {
    data.push({ label: 'Failed', value: status.failed, color: '#d32f2f' });
  }
  if(status.pending > 0) {
    data.push({ label: 'Pending', value: status.pending, color: '#ed6c02' });
  }
  if(status.aborted > 0) {
    data.push({ label: 'Aborted', value: status.aborted, color: '#ca8a04' });
  }
  if(status.skipped > 0) {
    data.push({ label: 'Skipped', value: status.skipped, color: '#9e9e9e' });
  }
  if(status.inProgress > 0) {
    data.push({ label: 'In Progress', value: status.inProgress, color: '#2196f3' });
  }
  
  const settings = {
    margin: { right: 5 },
    width: 140,
    height: 140,
    hideLegend: true,
  };

  return (
    <OverviewCard
      title="Last Execution Status"
      value={getStatusDisplayValue(status.overall)}
      icon={<ChartBarIcon className='icon' />}
      color="warning"
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        {/* Gauge Chart */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
        <PieChartPro
          series={[{ innerRadius: 30, outerRadius: 60, data, arcLabel: 'value' }]}
          {...settings}
          sx={{
            '& .MuiPieArcLabel-root': {
              fill: '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 600,
            },
          }}
        />
        </Box>

        {/* Status Breakdown */}
        {/* <Stack spacing={0.5} sx={{ width: "100%" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main" }} />
              <Typography variant="caption" color="#475569">
                Passed
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600}>
              {status.passed}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main" }} />
              <Typography variant="caption" color="#475569">
                Failed
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600}>
              {status.failed}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main" }} />
              <Typography variant="caption" color="#475569">
                Pending
              </Typography>
            </Stack>
            <Typography variant="caption" fontWeight={600}>
              {status.pending}
            </Typography>
          </Stack>
        </Stack> */}
      </Box>
      
      {/* Last Run Info */}
      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <Typography variant="caption" color="#475569" fontSize="10px">
          Last run: {dateFormat(status.lastRunDate)}
        </Typography>
      </Box>
    </OverviewCard>
  )
}

function TestCaseGrowthCard({ summary }) {
  const growth = Array.isArray(summary.growth) ? summary.growth : [];
  const data = growth.map(d => d.count);
  const labels = growth.map(d => d.date.slice(5)); // MM-DD

  return (
    <OverviewCard
      title="Test Case Growth"
      value={`${summary.growthRate || 0}%`}
      icon={<ChartBarIcon className='icon' />} 
      color="secondary"
      trendLabel="Current total:"
      trendValue={`${summary.total} test cases`}
    >
      <Box sx={{ height: 100, width: '100%' }}>
        <LineChart
          xAxis={[{ data: labels, scaleType: 'point', label: 'Date' }]}
          series={[{ data, label: 'Test Cases', color: '#21c45d' }]}
          height={80}
          margin={{ top: 8, bottom: 16, left: 10, right: 32 }}
          tooltip
          legend={false}
        />
      </Box>
    </OverviewCard>
  );
}

function RuleDefinition({ group }) {
  if (group.groupType !== "Dynamic") return null

  const { filterCriteria } = group

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        bgcolor: "#f8fafc",
        // border: "1px solid #e2e8f0",
        // borderRadius: 2,
      }}
    >
      <Typography variant="subtitle2" color="#475569" fontWeight={600} mb={2}>
        Rule Definition
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        {filterCriteria.priority && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="#475569" fontWeight={400}>
              Priority:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {filterCriteria.priority.map((p, i) => (
                <CustomChip
                  key={i}
                  label={p}
                  size="small"
                  color="info"
                  textColor="info.main"
                  borderColor="info.light"
                  fontWeight={400}
                  fontSize="0.75rem"
                  border={false}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {filterCriteria.module && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="#475569" fontWeight={400}>
              Module:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {filterCriteria.module.map((m, i) => (
                <CustomChip
                  key={i}
                  label={m}
                  size="small"
                  color="info"
                  textColor="info.main"
                  borderColor="info.light"
                  fontWeight={400}
                  fontSize="0.75rem"
                  border={false}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {filterCriteria.status && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="#475569" fontWeight={400}>
              Status:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {filterCriteria.status.map((s, i) => (
                <CustomChip
                  key={i}
                  label={s}
                  size="small"
                  color="info"
                  textColor="info.main"
                  borderColor="info.light"
                  fontWeight={400}
                  fontSize="0.75rem"
                  border={false}
                />
              ))}
            </Stack>
          </Stack>
        )}

        {filterCriteria.tags && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="#475569" fontWeight={400}>
              Tags:
            </Typography>
            <Stack direction="row" spacing={0.5}>
              {filterCriteria.tags.slice(0, 2).map((t, i) => (
                <CustomChip
                  key={i}
                  label={`#${t}`}
                  size="small"
                  color="info"
                  textColor="info.main"
                  borderColor="info.light"
                  fontWeight={400}
                  fontSize="0.75rem"
                  border={false}
                />
              ))}
              {filterCriteria.tags.length > 2 && (
                <CustomChip
                  label={`+${filterCriteria.tags.length - 2} more`}
                  size="small"
                  color="grey"
                  textColor="text.secondary"
                  height={24}
                  rounded={false}
                  fontWeight={400}
                  fontSize="0.75rem"
                  border={false}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

export default function TestGroupDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedProject } = useSelector((state) => state.projects);
  const [selectedModule, setSelectedModule] = useState("default")
  const [testCases, setTestCases] = useState([])
  // Pagination snaps back to page 0 when the selected module changes.
  const [paginationModel, setPaginationModel] = useResettablePagination({ pageSize: 100, resetKey: selectedModule })
  const [sortModel, setSortModel] = useState([])
  const [filterModel, setFilterModel] = useState({})
  const [selectedRows, setSelectedRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [launchModalOpen, setLaunchModalOpen] = useState(false)
  const { group_id: groupId } = useParams();
  const [loadingGroup, setLoadingGroup] = useState(false)
  const [loadingOverview, setLoadingOverview] = useState(true)
  const isInitialMount = useRef(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [moveToPositionOpen, setMoveToPositionOpen] = useState(false);
  const [moveTargetRow, setMoveTargetRow] = useState(null);
  const [movePositionValue, setMovePositionValue] = useState('');

  const [group, setGroup] = useState(null);
  const [moduleTree, setModuleTree] = useState([]);
  const [summary, setSummary] = useState({ total: 0, automated: 0, manual: 0, growth: [] });
  const [analytics, setAnalytics] = useState({ testLaunchesLast3Months: { total: 0, byMonth: [], trend: '0%' }, executionStatus: { passed: 0, failed: 0, pending: 0, aborted: 0, inProgress: 0, skipped: 0, overall: 'Pending', lastRunDate: null } });
  const [launchTotal, setLaunchTotal] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addTestCaseModalOpen, setAddTestCaseModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const launchesTableRef = useRef();
  const { showSnackbar } = useSnackbar();
    const activePage = useSelector((state) => state.navigation.activePage);
  const [splitSizes, setSplitSizes] = useState([23, 77]);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const moduleTreeRef = useRef();
  const tableContainerRef = useRef();
  const theme = useTheme();
  const routeParams = useQueryParams();

  // Responsive maxSize for Split component using custom breakpoints
  const isXxl = useMediaQuery(`(min-width:${breakpoints.xxl}px)`);
  const isXl = useMediaQuery(`(min-width:${breakpoints.xl}px)`);
  const isLg = useMediaQuery('(min-width:1200px)');
  const isMd = useMediaQuery('(min-width:900px)');
  
  const getResponsiveMaxSize = () => {
    if (isXxl) return [450, Infinity]; // xxl and above (1920px+)
    if (isXl) return [400, Infinity]; // xl and above (1440px+)
    if (isLg) return [350, Infinity]; // lg screens (1200px+)
    if (isMd) return [300, Infinity]; // md screens (900px+)
    return [280, Infinity]; // sm and below
  };

  useEffect(() => {
    if (routeParams && routeParams.get("launch_details") === "true") {
      setActiveTab(1);
    }
  }, [routeParams]);

  useEffect(() => {
    async function fetchGroup() {
      setLoadingGroup(true)
      try {
        const response = await getTestGroupById({ id: groupId });
        if (response.status === STATUS.SUCCESS) {
          setGroup(response.data || null);
        } else {
          console.error("Error fetching group:", response.error);
          setGroup(null);
        }
      } catch (e) {
        console.error("Error fetching group:", e)
        setGroup(null);
      }
      setLoadingGroup(false)
    }
    fetchGroup();
  }, [groupId]);


  // Fetch associated test cases for the group (and module)
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getAssociatedTestCasesForGroup({
        testGroupId: groupId,
        moduleId: selectedModule !== 'default' ? selectedModule : undefined,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortField: sortModel.length > 0 ? (sortModel[0].field === 'key' ? 'key_counter' : sortModel[0].field) : undefined,
        sortOrder: sortModel.length > 0 ? sortModel[0].sort : undefined,
      });
      
      if (response.status === STATUS.SUCCESS) {
        setTestCases(response.data?.testCases || []);
        setTotalRowCount(response.data?.pagination?.total || 0);
      } else {
        setTestCases([]);
        setTotalRowCount(0);
      }
    } catch (e) {
      setTestCases([]);
      setTotalRowCount(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [groupId, selectedModule, paginationModel.page, paginationModel.pageSize, sortModel]);

  // Fetch module tree for the group
  const fetchModules = async () => {
    if (!groupId) return;
    const response = await fetchModulesTestGroupWithCount({ testGroupId: groupId });
    if (response.status === STATUS.SUCCESS) {
      setModuleTree(response.data?.modulesData || []);
    } else {
      setModuleTree([]);
    }
  };

  useEffect(() => {
    fetchModules();
  }, [groupId]);

  // Add 'All Test Cases' node to the module tree
  const moduleTreeWithAll = useMemo(() => [
    { id: 'default', name: 'All Test Cases', children: [], individualcount: testCases.length, totalcount: testCases.length },
    ...moduleTree
  ], [moduleTree, testCases.length]);

  // Default selected module is 'all'
  useEffect(() => {
    setSelectedModule('default');
  }, [groupId]);

  // Fetch group summary and analytics for the cards (always group-level, not module-specific)
  useEffect(() => {
    async function fetchOverviewData() {
      if (!groupId) return;
      setLoadingOverview(true);
      try {
        const [summaryRes, analyticsRes, launchesRes] = await Promise.all([
          getTestGroupSummary({ testGroupId: groupId }),
          getTestGroupAnalytics({ testGroupId: groupId }),
          getTestLaunchesForGroup({ testGroupId: groupId, page: 1, pageSize: 1 }),
        ]);

        if (summaryRes.status === STATUS.SUCCESS) {
          // API returns data directly (not nested in .summary)
          setSummary(summaryRes.data || { total: 0, automated: 0, manual: 0, growth: [] });
        }
        
        if (analyticsRes.status === STATUS.SUCCESS) {
          // API returns data directly (not nested in .analytics)
          setAnalytics(analyticsRes.data || { 
            testLaunchesLast3Months: { total: 0, byMonth: [], trend: '0%' }, 
            executionStatus: { passed: 0, failed: 0, pending: 0, aborted: 0, inProgress: 0, skipped: 0, overall: 'Pending', lastRunDate: null } 
          });
        }

        if (launchesRes.status === STATUS.SUCCESS) {
          setLaunchTotal(launchesRes.data?.total ?? 0);
        } else {
          setLaunchTotal(0);
        }
      } catch (error) {
        console.error("Error fetching overview data:", error);
      } finally {
        setLoadingOverview(false);
      }
    }
    fetchOverviewData();
  }, [groupId]);

  // Refresh data when switching tabs
  useEffect(() => {
    if (!groupId) return;
    
    // Skip refresh on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Refresh data based on active tab
    if (activeTab === 0) { // Overview tab
      // Refresh all overview data using the helper function
      refreshAllData();
    }
    // Tab 1 (Test Launches) will refresh its own data via LaunchesTable component
  }, [activeTab, groupId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    routeParams.delete("launch_details");
    const newSearch = routeParams.toString();
    navigate({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : '',
    }, { replace: true });
  };

  // Split helpers: toggle collapse/expand of left module panel
  const toggleLeftPanel = () => {
    setSplitSizes((prev) => {
      const collapsed = prev[0] === 0;
      setIsLeftCollapsed(!collapsed);
      return collapsed ? [23, 77] : [0, 100];
    });
  };

  const handleLaunchSubmit = (data) => {
     setActiveTab(1);
    // setLaunchModalOpen(false)
    // Refresh analytics data to update launch counts and execution status
    setActiveTab(1);
    if (groupId) {
      // refreshAllData();
      // Also refresh the launches table
      if (launchesTableRef.current) {
        launchesTableRef.current.refresh();
      }
    }
  }

  // Helper function to refresh all data
  const refreshAllData = async () => {
    setLoadingOverview(true);
    try {
      const [summaryRes, analyticsRes, launchesRes] = await Promise.all([
        getTestGroupSummary({ testGroupId: groupId }),
        getTestGroupAnalytics({ testGroupId: groupId }),
        getTestLaunchesForGroup({ testGroupId: groupId, page: 1, pageSize: 1 }),
      ]);
      
      if (summaryRes.status === STATUS.SUCCESS) {
        setSummary(summaryRes.data || { total: 0, automated: 0, manual: 0, growth: [] });
      }
      
      if (analyticsRes.status === STATUS.SUCCESS) {
        setAnalytics(analyticsRes.data || { testLaunchesLast3Months: { total: 0, byMonth: [], trend: '0%' }, executionStatus: { passed: 0, failed: 0, pending: 0, aborted: 0, inProgress: 0, skipped: 0, overall: 'Pending', lastRunDate: null } });
      }

      if (launchesRes.status === STATUS.SUCCESS) {
        setLaunchTotal(launchesRes.data?.total ?? 0);
      } else {
        setLaunchTotal(0);
      }
      
      // Also refresh test cases and modules
      fetchData();
      fetchModules();
    } catch (error) {
      console.error("Error refreshing all data:", error);
    } finally {
      setLoadingOverview(false);
    }
  };

  // Handler to refresh group header after edit (name/description only)
  const handleEditGroup = (updatedFields) => {
    setEditModalOpen(false);
    if (updatedFields) {
      setGroup((prev) => (prev ? { ...prev, ...updatedFields } : prev));
    }
  };

  // Handler for successful add test cases
  const handleAddTestCasesSuccess = (isCloseModel = false) => {
    if (isCloseModel) {
      setAddTestCaseModalOpen(false);
    }
    // Refetch all data to update all widgets
    refreshAllData();
  };

  // Function to handle deletion of a test case
  const handleDelete = (testCase) => {
    setSelectedTestCase(testCase);
    setDeleteModalOpen(true);
  };

  // Function to confirm deletion of a test case
  const confirmDelete = async () => {
    try {
      const testCaseToDelete = selectedRows.length > 0 ? selectedRows : [selectedTestCase.id];
      const response = await removeTestCasesFromTestGroup({
        testGroupId: groupId,
        testCaseIds: testCaseToDelete
      });
      
      if (response.status === STATUS.SUCCESS) {
        setDeleteModalOpen(false);
        setSelectedTestCase(null);
        setSelectedRows([]);
        
        // Refresh all data after successful removal
        refreshAllData();
        showSnackbar(response.message || 'Test cases removed from group successfully', 'success');
      } else {
        showSnackbar(response.error || "Failed to remove test cases", "error");
      }
    } catch (error) {
      console.error('Error removing test cases:', error);
      showSnackbar(error.message || "Failed to remove test cases", "error");
    }
  };

  // Row action buttons for test case row
  const rowActionButtons = [
    {
      name: 'Move to First',
      tooltip: 'Move to first position',
      icon: <ArrowUpIcon className="icon" />,
      action: (row) => handleMoveToFirst(row),
      disabled: false,
    },
    {
      name: 'Move to Last',
      tooltip: 'Move to last position',
      icon: <ArrowDownIcon className="icon" />,
      action: (row) => handleMoveToLast(row),
      disabled: false,
    },
    {
      name: 'Move to Position...',
      tooltip: 'Move to a specific position',
      icon: <ArrowsUpDownIcon className="icon" />,
      action: (row) => handleMoveToPositionOpen(row),
      disabled: false,
    },
    {
      name: 'Remove',
      tooltip: 'Remove from group',
      icon: <TrashIcon className="icon" />,
      action: (row) => handleDelete(row),
      disabled: false,
    },
  ];

  // Module explorer header action buttons
  const moduleHeaderActionButtons = [
    {
      name: "Expand All",
      icon: <ChevronDownIcon className="icon" />,
      action: () => moduleTreeRef.current?.triggerExpandAll(moduleTree),
      disabled: moduleTree.length === 0 || isExpanded,
    },
    {
      name: "Collapse All", 
      icon: <ChevronUpIcon className="icon" />,
      action: () => moduleTreeRef.current?.triggerCollapseAll(),
      disabled: moduleTree.length === 0 || !isExpanded,
    },
  ];

  // Custom drag preview: show test case title in a card instead of UUID
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleDragStart = (e) => {
      const row = e.target.closest('.MuiDataGrid-row');
      if (!row) return;

      const rowId = row.getAttribute('data-id');
      const testCase = testCases.find(tc => tc.id === rowId);
      if (!testCase) return;

      const preview = document.createElement('div');
      preview.textContent = testCase.title || 'Test Case';
      Object.assign(preview.style, {
        padding: '8px 16px',
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '13px',
        fontFamily: 'inherit',
        color: '#1e293b',
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
      });
      document.body.appendChild(preview);
      e.dataTransfer.setDragImage(preview, 0, 0);

      // Clean up the preview element after drag starts
      requestAnimationFrame(() => document.body.removeChild(preview));
    };

    container.addEventListener('dragstart', handleDragStart, true);
    return () => container.removeEventListener('dragstart', handleDragStart, true);
  }, [testCases]);

  // Move to position handlers
  const handleMoveToFirst = async (row) => {
    const response = await reorderTestCaseInGroup({
      testGroupId: groupId,
      testcaseId: row.id,
      newSortOrder: 1,
    });
    if (response.status === STATUS.SUCCESS) {
      showSnackbar('Test case moved to first position', 'success');
      fetchData();
    } else {
      showSnackbar('Failed to move test case', 'error');
    }
  };

  const handleMoveToLast = async (row) => {
    const response = await reorderTestCaseInGroup({
      testGroupId: groupId,
      testcaseId: row.id,
      newSortOrder: totalRowCount,
    });
    if (response.status === STATUS.SUCCESS) {
      showSnackbar('Test case moved to last position', 'success');
      fetchData();
    } else {
      showSnackbar('Failed to move test case', 'error');
    }
  };

  const handleMoveToPositionOpen = (row) => {
    setMoveTargetRow(row);
    setMovePositionValue('');
    setMoveToPositionOpen(true);
  };

  const handleMoveToPositionConfirm = async () => {
    const position = parseInt(movePositionValue, 10);
    if (!position || position < 1 || position > totalRowCount) {
      showSnackbar(`Enter a valid position between 1 and ${totalRowCount}`, 'error');
      return;
    }
    setMoveToPositionOpen(false);
    const response = await reorderTestCaseInGroup({
      testGroupId: groupId,
      testcaseId: moveTargetRow.id,
      newSortOrder: position,
    });
    if (response.status === STATUS.SUCCESS) {
      showSnackbar(`Test case moved to position ${position}`, 'success');
      fetchData();
    } else {
      showSnackbar('Failed to move test case', 'error');
    }
    setMoveTargetRow(null);
  };

  // Header action handlers
  const handleBulkDelete = () => {
    setDeleteModalOpen(true);
  };

  const handleRowOrderChange = async (params) => {
    const { row, oldIndex, targetIndex } = params;
    // Compute absolute sort_order from page-relative index
    const pageOffset = paginationModel.page * paginationModel.pageSize;
    const newSortOrder = pageOffset + targetIndex + 1;

    // Optimistically reorder local state
    const updatedRows = [...testCases];
    const [movedRow] = updatedRows.splice(oldIndex, 1);
    updatedRows.splice(targetIndex, 0, movedRow);
    setTestCases(updatedRows);

    const response = await reorderTestCaseInGroup({
      testGroupId: groupId,
      testcaseId: row.id,
      newSortOrder,
    });

    if (response.status !== STATUS.SUCCESS) {
      showSnackbar('Failed to reorder test case', 'error');
      // Revert by re-fetching from server
      fetchData();
    }
  };

  // Define table columns inside component to access rowActionButtons
  const tableColumns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',        
    },
    {
      field: "orderIndex",
      headerName: "#",
      minWidth: 40,
      maxWidth: 60,
      flex: 0.4,
      sortable: false,
      filterable: false,
      hideable: false,
      renderCell: (params) => {
        const rowIndex = testCases.findIndex((tc) => tc.id === params.row.id);
        const pageOffset = paginationModel.page * paginationModel.pageSize;
        return rowIndex >= 0 ? pageOffset + rowIndex + 1 : '';
      },
    },
    {
      field: "key",
      headerName: "Test ID",
      minWidth: 100,
      maxWidth: 120,
      flex: 0.8,
      renderCell: (params) => {
        const { id, module_id } = params.row || {};
        let testCaseUrl = "#";
        const projectId = selectedProject?.id;
        if (id && module_id && projectId) {
          const searchParams = new URLSearchParams({
            moduleId: module_id,
            testCaseId: id,
          });
          testCaseUrl = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${projectId}${DEFAULT_ROUTES.TEST_CASES}?${searchParams.toString()}`;
        }
        return (
          renderTableCell(params.value, "link", {
            type: 'link',
            to: testCaseUrl,
          })
        );
      }
    },
    { 
      field: "title", 
      headerName: "Test Summary", 
      minWidth: 250,
      flex: 2,
      hideable: false,
      renderCell: (params) => renderTableCell(params.value),
    },
    { 
      field: "priority", 
      headerName: "Priority", 
      minWidth: 100,
      maxWidth: 120,
      flex: 0.8,
      renderCell: (params) => (
        <ChipComponent
          variant={getPriorityVariant(params.value)}
        >
          {params.value}
        </ChipComponent>
      ),
    },
    { 
      field: "type", 
      headerName: "Type", 
      minWidth: 160,
      maxWidth: 190,
      flex: 0.8,
      renderCell: (params) => (
        <ChipComponent
          variant={getTestCaseTypeVariant(params.value)}
          size="small"
        >
          {params.value}
        </ChipComponent>
      ),
    },
    {
      field: "lastRunStatus",
      headerName: "Last Run Status",
      minWidth: 120,
      maxWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return MissingData.NONE;

        return (
          <ChipComponent
            variant={getTestGroupStatusVariant(params.value)}
          >
            {formatStatusKey(params.value)}
          </ChipComponent>
        );
      },
    },
    {
      field: "lastRunDate",
      headerName: "Last Run Date",
      minWidth: 120,
      maxWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : MissingData.NONE,
    },
    {
      field: 'automation_status',
      headerName: 'Automation Status',
      minWidth: 170,
      maxWidth: 180,
      flex : 1,
      headerAlign: 'left',
      renderCell: (params) => {
        return (
          <ChipComponent variant={getAutomationStatusVariant(params?.row?.automation_status)} size="small">
            {params?.row?.automation_status}
          </ChipComponent>
        )
      },
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      hideable: false,
      minWidth: 55,
      maxWidth: 90,
      resizable: false,
      disableColumnMenu: true,
      flex: 0,  
      headerAlign: 'center',
      sortable: false,
      align: 'center',
      filterable: false,
      renderCell: (params) => (
        <RowActionMenu
          menuItems={rowActionButtons}
          rowData={params.row}
          isDisabled={false}
        />
      ),
    },
  ];

  if (loadingGroup || !group) {
    return <LoadingSpinner message="Loading Test Group..." />;
  }

  const hasNoTestCases = summary.total === 0;
  const hasLaunches = launchTotal > 0;
  const showLaunchNudge = !hasNoTestCases ;
  const showOverviewCards = !loadingGroup && !loadingOverview;

  // --- TabView tabsData definition ---
  const tabsData = [
    {
      name: 'Overview',
      icon: null, // Optionally add an icon here
      component: (
        <div
          className={
            hasNoTestCases && showOverviewCards
              ? `${styles['main-content-area']} ${styles['main-content-area--placeholder']}`
              : styles['main-content-area']
          }
        >
          <Box sx={{ bgcolor: '#f8fafc', pt: 2, pb: 0, px: 2, height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Overview Cards */}
            {showOverviewCards && (
              hasNoTestCases && !hasLaunches ? (
                <div className={`${styles['overview-container']} ${styles['overview-container--three-columns']} ${styles['overview-container--placeholder-cards']}`}>
                  {OVERVIEW_EMPTY_CARDS.map((card) => {
                    const Icon = card.icon;
                    return (
                      <OverviewCard
                        key={card.id}
                        title={card.title}
                        icon={<Icon className="icon" />}
                        color={card.color}
                      >
                        <OverviewEmptyCardContent
                          visualType={card.visualType}
                          description={card.description}
                        />
                      </OverviewCard>
                    );
                  })}
                </div>
              ) : (
                <div
                  className={
                    hasLaunches
                      ? styles['overview-container']
                      : `${styles['overview-container']} ${styles['overview-container--three-columns']}`
                  }
                >
                  <TestCaseSummaryCard summary={summary} />
                  <TagBreakdownCard moduleTree={moduleTree} total={summary.total} />
                  <TestCaseGrowthCard summary={summary} />
                  {hasLaunches && (
                    <>
                      <TestLaunchesCard launches={analytics.testLaunchesLast3Months} />
                      <ExecutionStatusCard status={analytics.executionStatus} />
                    </>
                  )}
                </div>
              )
            )}
            {/* Split view: Module Explorer + Table using react-split */}
            {/* <div className={styles['split-container']}> */}
            <Split
              sizes={splitSizes}
              minSize={260}
              maxSize={getResponsiveMaxSize()}
              gutterSize={18}
              gutterAlign="center"
              snapOffset={0}
              onDragEnd={(sizes) => setSplitSizes(sizes)}
              style={{ display: "flex", height: "100%" , maxHeight: "60vh", minHeight: "56vh"  }}
              gutter={(index, direction) => {
                const gutterElement = document.createElement("div");
                gutterElement.className = `gutter gutter-${direction}`;
                gutterElement.ondblclick = () => toggleLeftPanel();
                return gutterElement;
              }}
            >
              {/* Left: Module Explorer */}
              <Box>
                <FolderDrawerTreeView
                  data={moduleTree}
                  title="Modules"
                  onLeafNodeClick={(nodeId) => setSelectedModule(nodeId)}
                  selected={[selectedModule]}
                  setSelected={setSelectedModule}
                  hideHeader={false}
                  showSearch={true}
                  smallView={true}
                  headerActionButtons={moduleHeaderActionButtons}
                  disableHeaderActions={false}
                  updateIsExpanded={(flag) => setIsExpanded(flag)}
                  ref={moduleTreeRef}
                />
              </Box>
              {/* Right: Table */}
              <Box ref={tableContainerRef} >
                {showLaunchNudge && (
                  <TestGroupLaunchNudgeBanner
                    caseCount={summary.total}
                    onCreateLaunch={() => setLaunchModalOpen(true)}
                  />
                )}
                <TableComponent
                  title="Test Group Cases"
                  headerActionButtons={[
                    {
                      name: "Delete",
                      tooltip: "Remove selected test case(s) from group",
                      icon: <TrashIcon className="icon" />,
                      action: handleBulkDelete,
                      disabled: selectedRows.length === 0,
                      section: 'left',
                    },
                    // {
                    //   name: "Export",
                    //   tooltip: "Export Test Case(s)",
                    //   icon: <ArrowDownTrayIcon className="icon" />,
                    //   action: () => {/* TODO: implement export handler */},
                    //   // disabled: testCases.length === 0 || loading,
                    //   disabled: true,
                    //   section: 'left',
                    //   contained: false,
                    // },
                    {
                      name: '+ Add Test Case',
                      tooltip: "Add Test Case to Group",
                      icon: <PlusIcon className="icon" />,
                      action: () => setAddTestCaseModalOpen(true),
                      section: 'left',
                      contained: true,
                    },
                  ]}
                  rows={testCases}
                  columns={tableColumns}
                  isCheckbox={true}
                  isRowSelection={true}
                  selectedRows={selectedRows}
                  idField="id"
                  handleRowSelection={(selectionModel)=> setSelectedRows(selectionModel)}
                  setSelectedRows={setSelectedRows}
                  loading={loadingGroup}
                  isColumnResizable={!loadingGroup}
                  isPagination={true}
                  paginationMode="server"
                  rowCount={totalRowCount}
                  paginationModel={paginationModel}
                  handleCustomPaginationPageChange={(event, page)=> setPaginationModel({...paginationModel, page: page})}
                  handleCustomPaginationPageSizeChange={(pageSize)=> setPaginationModel({page: 0, pageSize: pageSize})}
                  isCustomPagination={true}
                  filterMode="client"
                  sortingMode="server"
                  sortModel={sortModel}
                  handleSortModelChange={(newSortModel) => {
                    setSortModel(newSortModel);
                    setPaginationModel(prev => ({ ...prev, page: 0 }));
                  }}
                  rowReordering={true}
                  onRowOrderChange={handleRowOrderChange}
                  disableFooter={false}
                  customStyles={{
                    '& .MuiDataGrid-cell': {
                      py: 1,
                    },
                    "& .selected-row": {
                      backgroundColor: theme.palette.customColor.blue[50],
                      "&:hover": {
                        backgroundColor: theme.palette.customColor.blue[100],
                      },
                    },
                  }}
                  hideColumns={['key']}
                  isData={!hasNoTestCases || loadingGroup || loadingOverview}
                  customNoData={(
                    <TestGroupCasesEmptyPanel
                      onAddTestCases={() => setAddTestCaseModalOpen(true)}
                    />
                  )}
                />
              </Box>
            </Split>
          {/* </div> */}
          </Box>
        </div>
      ),
    },
    {
      name: 'Test Launches',
      icon: null, // Optionally add an icon here
      component: (
        <Box sx={{ bgcolor: '#f8fafc', pt: 2, pb: 0, px: 0, height: "100%", width: "100%" }}>
          <LaunchesTable
            ref={launchesTableRef}
            groupId={groupId}
            onCreateLaunch={() => setLaunchModalOpen(true)}
            isCreateLaunchDisabled={hasNoTestCases}
          />
        </Box>
      ),
    },
  ];
  
  
  function handleBreadcrumbClick(id) {
  let rURL = "";
  let activePage = "";

  switch (id) {
    case "home":
      rURL = DEFAULT_ROUTES.BASE_URL;
      activePage = "Projects";
      break;
    case "project":
      rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-cases`;
      activePage = "Test Cases";
      break;
    case "test-group":
      rURL = `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject?.id}/test-groups`;
      activePage = "Test Groups";
      break;
    default:
      return;
  }

  if(activePage) {
    dispatch(setActivePage(activePage));
    navigate(rURL);
  }
}

const breadcrumbItems = [
  {
    label: "Project",
    onClick: () => handleBreadcrumbClick("home"),
  },
  {
    label: selectedProject?.name || "Unknown Project",
  },
  {
    label: activePage,
    onClick: () => handleBreadcrumbClick("test-group"),
  },
  {
    label: group?.key,
  },
];

const onCloseLaunchModal = () => {
    setLaunchModalOpen(false);
  }

  return (
    <Box className={commonStyles['page-container']}>
      <Box>
        <PageHeader
          projectName={selectedProject?.name}
          title={group.name}
          breadcrumbItems={breadcrumbItems}
          subTitleComponent={
            <Stack spacing={1.5} mt={0.5}>
              {/* ID and Type Row */}
              <Stack direction="row" spacing={3} alignItems="center">
                <Typography variant="body2" color="#475569">
                  ID: {group?.key}
                </Typography>
                <CustomChip
                  label={group?.group_type}
                  size="small"
                  color={group?.group_type === "Static" ? "#DBEAFE" : "#EDE9FE"}
                  textColor={group?.group_type === "Static" ? "#1E40AF" : "#6B21A8"}
                  borderColor={group?.group_type === "Static" ? "#BFDBFE" : "#DDD6FE"}
                  fontWeight={600}
                  rounded={true}
                  fontSize="0.75rem" />
              </Stack>
              
              {/* Description */}
              {group?.description && (
                <Typography variant="body2" color="#64748b" sx={{ maxWidth: '800px', lineHeight: 1.8 }} title={group.description} className="ellipsis-container-line-2">
                  {group.description}
                </Typography>
              )}
            </Stack>
          }
          actions={[
            <ButtonComponent
              key="edit"
              variant="outline"
              startIcon={<PencilIcon className="icon" />}
              onClick={() => setEditModalOpen(true)}
              ctaText="Edit Group"
              className={`${styles['action-button']} ${styles['outlined']}`}
            />,
            <ButtonComponent
              key="launch"
              variant="primary"
              disabled={hasNoTestCases}
              startIcon={<PlayIcon className="icon" />}
              onClick={() => setLaunchModalOpen(true)}
              ctaText="Create Launch"
              className={`${styles['action-button']} ${styles['primary']}`}
            />
          ]}
        />
      </Box>
      <Box className={commonStyles['cover-container-col']}>
      <TabView tabsData={tabsData} onTabChange={handleTabChange} parentActiveTab={activeTab} />
      <Divider sx={{ mb: 3 }} />
      {/* <Box sx={{ bgcolor: 'background.paper' }}>
      </Box>
        <Box className={styles['tabs-section']}>
          </Box> */}
      </Box>


      {/* popups */}
      <AddTestCasesToGroupModal
        open={addTestCaseModalOpen}
        onClose={() => setAddTestCaseModalOpen(false)}
        testGroup={group}
        onSuccess={handleAddTestCasesSuccess}
        currentTestCaseCount={totalRowCount}
      />
      {/* Edit Group Modal */}
      <CreateTestGroupModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEdit={handleEditGroup}
        editMode={true}
        initialValues={group ? {
          id: group.id,
          name: group.name,
          description: group.description,
          next_scheduled_run: group.next_scheduled_run || group.nextRunDate || null,
        } : {}}
        // projectId={selectedProject?.id} // Uncomment if you have projectId from redux
      />
      {/* Create Launch Modal */}
      <CreateTestLaunchModal
        open={launchModalOpen}
        onClose={onCloseLaunchModal}
        onSubmit={handleLaunchSubmit}
        group={group}
        projectId={group?.project_id}
      />

      {/* Remove Test Cases Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Remove Test Cases"
        message={
          selectedRows.length > 0
            ? `Are you sure you want to remove ${selectedRows.length} test case(s) from this test group? This action cannot be undone.`
            : `Are you sure you want to remove this test case from this test group? This action cannot be undone.`
        }
      />

      {/* Move to Position Modal */}
      <ModalWrapper
        isOpen={moveToPositionOpen}
        onClose={() => setMoveToPositionOpen(false)}
        title="Move to Position"
        size="small"
        border={false}
        footer={
          <CommonFooter
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setMoveToPositionOpen(false),
                variant: 'outline',
                className: 'primary',
              },
              {
                label: 'Move',
                onClick: handleMoveToPositionConfirm,
                variant: 'primary',
                className: 'white bg-blue600',
                isdefault: true,
              },
            ]}
            align="right"
          />
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Moving: <strong>{moveTargetRow?.title}</strong>
          </Typography>
          <TextField
            autoFocus
            label="Position"
            type="number"
            fullWidth
            size="small"
            value={movePositionValue}
            onChange={(e) => setMovePositionValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleMoveToPositionConfirm(); }}
            inputProps={{ min: 1, max: totalRowCount }}
            helperText={`Enter a position between 1 and ${totalRowCount}`}
          />
        </Box>
      </ModalWrapper>
    </Box>
  )
}
